import { useCallback, useState, createContext, useContext, useMemo } from 'react';
import { ContextData, ContextProviderProps } from 'types/context';
import { ListJobs } from '../api/jobs-endpoint';
import { JobIdentifier } from '../types/job';

const JobsContext = createContext<ContextData<JobIdentifier> | undefined>(undefined);
JobsContext.displayName = 'JobContext';

function useJobContextData(): ContextData<JobIdentifier> {
  const [jobs, setJobs] = useState<JobIdentifier[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobIdentifier[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    const listJobsResponse = await ListJobs();

    if (listJobsResponse.success && listJobsResponse.data) {
      setJobs(listJobsResponse.data);
    } else {
      setJobs([]);
    }
    setIsLoading(false);
  }, [setJobs]);

  const filterJobs = useCallback(
    async (filter: string) => {
      if (filter === '') {
        setFilteredJobs(undefined);
        return;
      }

      const filterResult = jobs.filter((job) => {
        return job.Name?.toLowerCase().includes(filter.toLowerCase()) || job.Description?.toLowerCase().includes(filter.toLowerCase());
      });

      setFilteredJobs(filterResult);
    },
    [setFilteredJobs, jobs],
  );

  return useMemo(
    () => ({
      data: filteredJobs ?? jobs,
      isLoading,
      fetchData: fetchJobs,
      filterData: filterJobs,
    }),
    [jobs, filteredJobs, isLoading, fetchJobs, filterJobs],
  );
}

export const useJobsContext = (): ContextData<JobIdentifier> => {
  const jobsContext = useContext(JobsContext);
  if (!jobsContext) {
    throw new Error('useJobsContext must be used within the JobsContext.Provider');
  }
  return jobsContext;
};

export function JobsContextProvider({ children }: ContextProviderProps): JSX.Element {
  const JobContextData = useJobContextData();
  return <JobsContext.Provider value={JobContextData}>{children}</JobsContext.Provider>;
}
export default { useJobsContext, JobsContextProvider };
